import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { e2eClonerAPI } from '../lib/api';
import { chunkedUpload, shouldUseChunkedUpload } from '../lib/chunkedUpload';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Trash2, Play, Loader2, CheckCircle, AlertCircle,
  Wand2, Copy, Video, Clock, RefreshCw, Tag, Camera, Film,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { cn } from '../lib/utils';

const projectStatusConfig = {
  pending: { label: 'No Analysis', color: 'bg-zinc-100 text-zinc-600' },
  analyzing: { label: 'Analyzing...', color: 'bg-blue-50 text-blue-700' },
  analyzed: { label: 'Analyzed', color: 'bg-emerald-50 text-emerald-700' },
};

const videoStatusConfig = {
  pending: { label: 'Pending', color: 'bg-zinc-100 text-zinc-600', icon: Clock, spin: false },
  processing: { label: 'Processing', color: 'bg-blue-50 text-blue-700', icon: Loader2, spin: true },
  done: { label: 'Done', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle, spin: false },
  error: { label: 'Error', color: 'bg-red-50 text-red-700', icon: AlertCircle, spin: false },
};

export default function E2EClonerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const fileInputRef = useRef(null);

  // Live polling while project is analyzing
  const pollTimerRef = useRef(null);

  const loadProject = useCallback(async () => {
    try {
      const res = await e2eClonerAPI.getProject(id);
      setProject(res.data);
    } catch {
      toast.error('Failed to load project');
      navigate('/research');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // Poll while analyzing
  useEffect(() => {
    if (!project) return;
    const isActive =
      project.status === 'analyzing' ||
      (project.videos || []).some(
        v => v.analysis_status === 'pending' || v.analysis_status === 'processing'
      );
    if (isActive) {
      pollTimerRef.current = setTimeout(() => loadProject(), 5000);
    }
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [project, loadProject]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 100 - (project?.videos?.length || 0);
    if (remaining <= 0) {
      toast.error('Project full (100 videos max)');
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    let ok = 0;
    for (let fi = 0; fi < toUpload.length; fi++) {
      const file = toUpload[fi];
      try {
        setUploadProgress(0);
        setUploadStatus(`Uploading ${fi + 1}/${toUpload.length}: ${file.name}`);
        if (shouldUseChunkedUpload(file)) {
          await chunkedUpload(file, 'e2e_cloner_video', id, (p) => setUploadProgress(p));
        } else {
          const fd = new FormData();
          fd.append('file', file);
          await e2eClonerAPI.uploadVideo(id, fd);
          setUploadProgress(100);
        }
        ok += 1;
      } catch (err) {
        console.error('Upload failed', err);
        toast.error(`${file.name}: ${err.response?.data?.detail || 'Upload failed'}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadStatus('');
    if (ok > 0) toast.success(`${ok} video${ok > 1 ? 's' : ''} uploaded`);
    if (fileInputRef.current) fileInputRef.current.value = '';
    loadProject();
  };

  const handleDeleteVideo = async (vidId) => {
    try {
      await e2eClonerAPI.deleteVideo(id, vidId);
      toast.success('Video removed');
      loadProject();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      const res = await e2eClonerAPI.analyze(id);
      const queued = res.data?.queued || 0;
      if (queued === 0) {
        toast.info('All videos already analyzed');
      } else {
        toast.success(`Analyzing ${queued} video${queued > 1 ? 's' : ''}...`);
      }
      loadProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReanalyzeVideo = async (vidId) => {
    try {
      await e2eClonerAPI.reanalyzeVideo(id, vidId);
      toast.success('Re-running analysis');
      loadProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-analyze');
    }
  };

  const handleResetRetry = async () => {
    try {
      setAnalyzing(true);
      const res = await e2eClonerAPI.reset(id);
      const queued = res.data?.queued || 0;
      if (queued === 0) toast.info('Nothing to reset — all videos done');
      else toast.success(`Reset & retry — ${queued} video${queued > 1 ? 's' : ''} requeued`);
      loadProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const copy = (text, label = 'Copied') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!project) return null;

  const videos = project.videos || [];
  const doneCount = videos.filter(v => v.analysis_status === 'done').length;
  const errorCount = videos.filter(v => v.analysis_status === 'error').length;
  const processingCount = videos.filter(
    v => v.analysis_status === 'processing' || v.analysis_status === 'pending'
  ).length;
  const projSt = projectStatusConfig[project.status] || projectStatusConfig.pending;
  const isAnalysisActive = project.status === 'analyzing' || processingCount > 0;

  // Detect "stuck": project says analyzing but no progress in >2 min. Lets us
  // surface a Reset & Retry CTA when a deploy/pod restart killed the bg task.
  const startedAt = project.analysis_started_at ? new Date(project.analysis_started_at).getTime() : null;
  const lastUpdatedAt = project.updated_at ? new Date(project.updated_at).getTime() : null;
  const lastSignal = Math.max(startedAt || 0, lastUpdatedAt || 0);
  const stuckMinutes = lastSignal && isAnalysisActive
    ? Math.floor((Date.now() - lastSignal) / 60000)
    : 0;
  const isStuck = isAnalysisActive && stuckMinutes >= 2;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12" data-testid="e2e-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={() => navigate('/research')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
          data-testid="back-to-research"
        >
          <ArrowLeft size={16} /> Research
        </button>
        <Badge className={`text-xs ${projSt.color}`}>{projSt.label}</Badge>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">{project.name}</h1>
        {project.notes && <p className="text-sm text-zinc-500 mt-1">{project.notes}</p>}
        <p className="text-xs text-zinc-400 mt-2">
          {videos.length} videos · {doneCount} analyzed · {errorCount} errors
          {project.created_by_name && <> · created by {project.created_by_name}</>}
        </p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="video/*"
          onChange={handleUpload}
          className="hidden"
          data-testid="e2e-upload-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || videos.length >= 100}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="e2e-upload-btn"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading...</>
          ) : (
            <><Upload size={15} className="mr-1.5" /> Upload Videos ({videos.length}/100)</>
          )}
        </Button>
        <Button
          onClick={handleAnalyze}
          disabled={analyzing || isAnalysisActive || videos.length === 0}
          className="bg-violet-600 text-white hover:bg-violet-700"
          data-testid="e2e-analyze-btn"
        >
          {isAnalysisActive ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analyzing... {doneCount + errorCount}/{videos.length}</>
          ) : (
            <><Wand2 size={15} className="mr-1.5" /> Run Analysis</>
          )}
        </Button>
        {isStuck && (
          <Button
            onClick={handleResetRetry}
            disabled={analyzing}
            className="bg-amber-600 text-white hover:bg-amber-700"
            data-testid="e2e-reset-retry-btn"
          >
            <RefreshCw size={15} className="mr-1.5" /> Reset & Retry
          </Button>
        )}
      </div>

      {/* Stuck warning */}
      {isStuck && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
          data-testid="e2e-stuck-warning"
        >
          <AlertCircle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-amber-900">
              Stuck for {stuckMinutes} min — analysis hasn't progressed.
            </p>
            <p className="text-xs text-amber-800/80 mt-1">
              The background task may have been killed by a server restart or deploy. Click
              <strong className="mx-1">Reset & Retry</strong>
              to re-queue the remaining videos. Already-analyzed videos are skipped.
            </p>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="flex items-center justify-between text-xs text-zinc-600 mb-2">
            <span>{uploadStatus}</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-zinc-900 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Empty state */}
      {videos.length === 0 && !uploading && (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <Video size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-400">Upload your batch of creator videos to get started</p>
          <p className="text-xs text-zinc-400 mt-1">Up to 100 videos per project. Large videos use chunked upload automatically.</p>
        </div>
      )}

      {/* Video list */}
      <div className="space-y-4" data-testid="e2e-video-list">
        {videos.map((v, idx) => (
          <VideoCard
            key={v.id}
            video={v}
            index={idx}
            onDelete={() => handleDeleteVideo(v.id)}
            onReanalyze={() => handleReanalyzeVideo(v.id)}
            onPreview={() => setPreviewVideo(v)}
            onCopy={copy}
          />
        ))}
      </div>

      {/* Video Preview Dialog */}
      <Dialog open={!!previewVideo} onOpenChange={() => setPreviewVideo(null)}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 text-white border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white truncate">{previewVideo?.original_name}</DialogTitle>
          </DialogHeader>
          {previewVideo?.download_url && (
            <video
              src={previewVideo.download_url}
              controls
              className="w-full rounded-md max-h-[70vh]"
              data-testid="e2e-video-preview"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== VideoCard ==============

function VideoCard({ video, index, onDelete, onReanalyze, onPreview, onCopy }) {
  const st = videoStatusConfig[video.analysis_status] || videoStatusConfig.pending;
  const StatusIcon = st.icon;
  const out = video.output;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg overflow-hidden"
      data-testid={`e2e-video-card-${video.id}`}
    >
      {/* Card Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-100 bg-zinc-50/50">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-md bg-violet-100 text-violet-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
            {String(index + 1).padStart(2, '0')}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-900 truncate">{video.original_name}</div>
            <div className="text-xs text-zinc-400">
              {(video.file_size / (1024 * 1024)).toFixed(1)} MB
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge className={`text-xs ${st.color}`}>
            <StatusIcon size={12} className={cn('mr-1', st.spin && 'animate-spin')} />
            {st.label}
          </Badge>
          {video.download_url && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
              onClick={onPreview}
              data-testid={`preview-video-${video.id}`}
              title="Preview video"
            >
              <Play size={14} />
            </Button>
          )}
          {(video.analysis_status === 'error' || video.analysis_status === 'done' || video.analysis_status === 'pending') && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-zinc-400 hover:text-zinc-900"
              onClick={onReanalyze}
              data-testid={`reanalyze-video-${video.id}`}
              title="Re-analyze this video"
            >
              <RefreshCw size={14} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-zinc-400 hover:text-red-600"
            onClick={onDelete}
            data-testid={`delete-video-${video.id}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Card Body */}
      <div className="px-5 py-4">
        {video.analysis_status === 'error' && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
            <strong>Analysis error:</strong> {video.analysis_error || 'Unknown error'}
          </div>
        )}

        {video.analysis_status === 'pending' && (
          <p className="text-xs text-zinc-400">Waiting for analysis. Click Run Analysis above.</p>
        )}

        {video.analysis_status === 'processing' && (
          <p className="text-xs text-blue-600 inline-flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Transcribing audio and rewriting script...
          </p>
        )}

        {video.analysis_status === 'done' && out && (
          <div className="space-y-4">
            {/* Hook callout */}
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
                  Original Hook (kept as-is)
                </span>
                <button
                  className="text-amber-700 hover:text-amber-900"
                  onClick={() => onCopy(out.original_hook, 'Hook copied')}
                  title="Copy hook"
                >
                  <Copy size={13} />
                </button>
              </div>
              <p className="text-sm text-amber-950 italic">"{out.original_hook}"</p>
            </div>

            {/* Side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ScriptColumn
                title="Original Script"
                text={out.original_script}
                accent="zinc"
                onCopy={() => onCopy(out.original_script, 'Original copied')}
                testid={`original-script-${video.id}`}
              />
              <ScriptColumn
                title="Rewritten Script"
                text={out.rewritten_script}
                accent="violet"
                onCopy={() => onCopy(out.rewritten_script, 'Rewritten copied')}
                testid={`rewritten-script-${video.id}`}
              />
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {out.format_tag && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-rose-50 text-rose-700 border border-rose-100"
                  data-testid={`format-tag-${video.id}`}
                >
                  <Tag size={11} /> {out.format_tag}
                </span>
              )}
              {out.on_camera && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                  data-testid={`on-camera-${video.id}`}
                >
                  <Camera size={11} /> {out.on_camera}
                </span>
              )}
            </div>

            {/* B-Roll Needed */}
            {Array.isArray(out.b_roll_needed) && out.b_roll_needed.length > 0 && (
              <div className="border-t border-zinc-100 pt-3">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">
                  <Film size={12} /> B-Roll Needed
                </div>
                <ul className="space-y-1">
                  {out.b_roll_needed.map((item, i) => (
                    <li key={i} className="text-sm text-zinc-700 flex gap-2">
                      <span className="text-zinc-400 flex-shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {video.analysis_status === 'done' && !out && video.raw_output && (
          <div className="text-xs text-zinc-500 whitespace-pre-wrap font-mono bg-zinc-50 border border-zinc-200 rounded p-3 max-h-60 overflow-y-auto">
            {video.raw_output}
          </div>
        )}
      </div>
    </div>
  );
}

// ============== ScriptColumn ==============

function ScriptColumn({ title, text, accent, onCopy, testid }) {
  const accentClasses = {
    zinc: 'bg-zinc-50 border-zinc-200 text-zinc-900',
    violet: 'bg-violet-50/60 border-violet-200 text-zinc-900',
  };
  const labelClasses = {
    zinc: 'text-zinc-600',
    violet: 'text-violet-700',
  };
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[11px] uppercase tracking-wider font-semibold ${labelClasses[accent]}`}>
          {title}
        </span>
        <button
          className="text-zinc-400 hover:text-zinc-900"
          onClick={onCopy}
          title="Copy"
        >
          <Copy size={13} />
        </button>
      </div>
      <div className={`text-sm leading-relaxed whitespace-pre-wrap rounded-md border px-4 py-3 max-h-96 overflow-y-auto ${accentClasses[accent]}`}>
        {text || '—'}
      </div>
    </div>
  );
}
